import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CartItemPriceDisplay from '@components/cart/CartItemPriceDisplay';
import CartItemRow from '@components/cart/CartItemRow';
import { Context } from '@components/Context';
import useDeliveryTimescale from '@components/dashboard/hooks/useDeliveryTimescale';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import IconWithTooltip, { TooltipButton } from '@components/shared/IconWithTooltip';
import { CurrencyObj } from '@contracts';
import ShippingIcon from '@images/cart/shipping.png';
import InfoIcon from '@images/common/infoIcon.svg';
import { getPriceText } from '@utils/price';

interface Props {
  originalShippingPrice: number;
  shippingPrice: number;
  currency: CurrencyObj;
  paidCartItemsCount: number;
}

const Shipping: React.FC<Props> = ({ originalShippingPrice, shippingPrice, currency, paidCartItemsCount }) => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { country } = state;
  const { getDeliveryTimescale } = useDeliveryTimescale();
  const timescale = getDeliveryTimescale();

  const priceText = shippingPrice > 0 ? getPriceText(shippingPrice, country, currency, t).fullString : t('components.free-in-cart');

  const originalPriceText =
    originalShippingPrice > 0 && shippingPrice < originalShippingPrice ? getPriceText(originalShippingPrice, country, currency, t).fullString : '';

  const titleText = paidCartItemsCount > 1 ? t('components.combined-shipping') : t('components.shipping');

  return (
    <CartItemRow
      imageComponent={
        <ShippingImageWrap>
          <ShippingImageStyled src={ShippingIcon} alt="shipping icon" />
        </ShippingImageWrap>
      }
      titleComponent={
        <TitleContainer>
          <ItemText $variant={TextVariant.BodyRegularMD}>
            <span>{titleText}</span>
            <span className="subtitle">
              {t('components.shipping-estimate', {
                timescale: `${timescale.timescaleMin} - ${timescale.timescaleMax}`
              })}
            </span>
          </ItemText>
          {paidCartItemsCount > 1 && (
            <IconWithTooltip text={t('components.shipping-info')}>
              <TooltipButton>
                <InfoIcon />
              </TooltipButton>
            </IconWithTooltip>
          )}
        </TitleContainer>
      }
      priceComponent={
        <PriceComponentWrapper>
          <CartItemPriceDisplay price={priceText} originalPrice={originalPriceText} textColor={Colors.navyBlue} testId="shipping-price" />
        </PriceComponentWrapper>
      }
      extraContent={null}
    />
  );
};

export default Shipping;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ItemText = styled(Typography)`
  display: flex;
  flex-direction: column;
  margin: 0;
  color: ${Colors.navyBlue};

  .subtitle {
    font-size: 13px;
    line-height: 16px;
    opacity: 0.5;
    margin-top: 4px;
  }
`;

const ShippingImageWrap = styled.div`
  width: 40px;
  min-width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 15px;
`;

const ShippingImageStyled = styled.img`
  display: block;
  width: 40px;
  height: 100%;
`;

const PriceComponentWrapper = styled.div`
  padding-right: 25px;

  .is-checkout & {
    padding-right: 0;
  }
`;
